// Client-side resume state persistence (Phase 5 Step 3).
//
// localStorage key `nexus-resume` holds the {epochId, lastSeenSeq}
// watermark the resume handshake consults on every WebSocket open.
// The network layer reads it to build the opening `resume` envelope,
// and the inbound dispatcher writes to it whenever a durable envelope
// advances the seq OR a `snapshot` reply rewrites the epoch.
//
// Pure logic over a narrow StorageLike interface so unit tests can
// exercise the paths without a real browser. The one place we bind to
// `window.localStorage` is the network layer; everything here is shape.

import {
  buildResumeMessage,
  type ClientResume,
} from "./types.ts";

// Same shape as the Web Storage API's minimum surface. Using a local
// type instead of lib.dom.Storage keeps the module import-clean under
// server vitest (no DOM lib required) and trims the test stub.
export type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

export type ResumeState = {
  epochId: string;
  lastSeenSeq: number;
};

export const RESUME_STORAGE_KEY = "nexus-resume";

// Returns null for any of: missing row, unparseable JSON, or a row
// whose shape doesn't match the expected {epochId: string,
// lastSeenSeq: number}. The caller treats all three cases identically
// — send a `fresh: true` resume and wait for a snapshot.
export function loadResumeState(storage: StorageLike): ResumeState | null {
  const raw = storage.getItem(RESUME_STORAGE_KEY);
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // contracts:boundary — a corrupted localStorage row is indistinguishable
    // from "no saved state" for our purposes; fall through to fresh bootstrap.
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const rec = parsed as Record<string, unknown>;
  if (typeof rec["epochId"] !== "string") return null;
  if (typeof rec["lastSeenSeq"] !== "number") return null;
  return { epochId: rec["epochId"], lastSeenSeq: rec["lastSeenSeq"] };
}

export function saveResumeState(storage: StorageLike, state: ResumeState): void {
  storage.setItem(RESUME_STORAGE_KEY, JSON.stringify(state));
}

export function clearResumeState(storage: StorageLike): void {
  storage.removeItem(RESUME_STORAGE_KEY);
}

// Picks the right `resume` envelope for the current storage state.
// Phase 5.1 Step 1: the first WebSocket open of a page lifecycle MUST
// always force a snapshot, regardless of localStorage contents. The
// resume protocol replays durable envelopes onto an existing in-memory
// world; on page reload the world is empty, so a `replay` reply with
// "you're caught up" leaves the client with a blank scene. The caller
// (NetworkClient) owns the per-session boolean and clears the stored
// token on the fresh path so the snapshot reply re-seeds it cleanly.
// Missing or corrupt row → fresh handshake (server must snapshot).
// Populated row → replay attempt (server decides replay vs snapshot
// fallback based on epoch match + seq horizon).
export function makeResumeHandshake(
  storage: StorageLike,
  hasHadOpenSocketThisSession: boolean,
): ClientResume {
  if (!hasHadOpenSocketThisSession) {
    clearResumeState(storage);
    return buildResumeMessage(undefined, undefined, true);
  }
  const state = loadResumeState(storage);
  if (state === null) return buildResumeMessage(undefined, undefined, true);
  return buildResumeMessage(state.epochId, state.lastSeenSeq);
}

// Advance the lastSeenSeq watermark after applying a durable envelope.
// Idempotent if called with the same seq twice; never regresses (a
// higher already-persisted seq is preserved — out-of-order delivery
// would be a server bug, but the read-before-write guard keeps the
// client self-consistent regardless).
export function advanceSeq(storage: StorageLike, seq: number): void {
  const existing = loadResumeState(storage);
  if (existing === null) return; // no epoch yet — wait for snapshot.
  if (seq <= existing.lastSeenSeq) return;
  saveResumeState(storage, { epochId: existing.epochId, lastSeenSeq: seq });
}
